import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { cn } from "../../lib/utils";
import { CitationChip } from "./CitationChip";
import { Tag, type TagVariant } from "./Tag";
import { type SourceTarget } from "./SourceLink";
import { SourceSignals } from "./SourceSignals";

/**
 * One row of the list. Flat and nullable field by field rather than a union
 * over provenance: every provenance fills the SAME four slots, and which of
 * them it can fill is a property of the retrieval, not of the layout — a PubMed
 * snapshot captured before journals were carried leaves `container` null the
 * same way an uploaded file does.
 */
export type ChatSource = {
  /** Stable identity, for React's key and nothing else. */
  key: string;
  /**
   * Where the work was published: the journal for a paper, the issuing agency
   * or society for a guideline, the host for a web page. `kind` picks the
   * preposition ("In Nature" / "By HAS" / "On nature.com") so a hostname never
   * reads as a publication of record. Null hides the line.
   */
  container: {
    kind: "journal" | "organization" | "website";
    name: string;
  } | null;
  title: string;
  /** "Kopetz S et al. · 2025" — "" when the source carries neither. */
  identity: string;
  /** The study-design chip. Null when the source states no design. */
  design: { label: string; variant: TagVariant } | null;
  /** "pending" while the api still resolves the source: a placeholder holds the design line, and the byline until one is known. */
  resolution: "pending" | "resolved";
  /** Confidence and Endorsement in [0,1]; each null when nothing states it, and then not shown. */
  signals: { quality: number | null; endorsement: number | null };
  /** Null when there is nothing to open — an uploaded file, a dead article. */
  target: SourceTarget | null;
};

// What a slot not yet known shows, in a row being resolved and in the skeleton.
export const SKELETON_PLACEHOLDER = "animate-pulse bg-ds-mint-200";

// The source line, one sentence per kind of container. Each case passes a
// string literal so the i18n scanner can extract it.
function ContainerLabel({
  kind,
  name,
  nameElement,
}: {
  kind: NonNullable<ChatSource["container"]>["kind"];
  name: string;
  nameElement: React.JSX.Element;
}): React.JSX.Element {
  switch (kind) {
    case "journal":
      return (
        <Trans
          i18nKey="In <name>{{name}}</name>"
          values={{ name }}
          components={{ name: nameElement }}
        />
      );
    case "organization":
      return (
        <Trans
          i18nKey="By <name>{{name}}</name>"
          values={{ name }}
          components={{ name: nameElement }}
        />
      );
    case "website":
      return (
        <Trans
          i18nKey="On <name>{{name}}</name>"
          values={{ name }}
          components={{ name: nameElement }}
        />
      );
    default:
      throw new Error(kind satisfies never);
  }
}

/**
 * The source's identity, in the grammar both surfaces state it in: where it was
 * published, its title, its first author and year, its study design. Shared so
 * a source reads identically in the list and in the sheet that opens from it;
 * only the title's size differs, which is what `size` picks.
 */
export function ChatSourceIdentity({
  source,
  size,
}: {
  source: ChatSource;
  // `rail` is a row in the narrow desktop context panel: the sheet's title size,
  // held to two lines so nine sources still read as a list.
  size: "row" | "rail" | "sheet";
}): React.JSX.Element {
  // The rail is the desktop panel's own type ramp (Figma "Source Item"): 13px
  // throughout, the journal in slate rather than mint, the authors at 12px.
  const isRail = size === "rail";
  return (
    <span className="flex min-w-0 flex-1 flex-col gap-1">
      {source.container !== null && (
        <span
          className={cn(
            "flex min-w-0 gap-1 text-footnote font-normal",
            isRail ? "text-ds-slate-700" : "text-ds-slate-600"
          )}
        >
          <ContainerLabel
            kind={source.container.kind}
            name={source.container.name}
            nameElement={
              <span
                className={cn(
                  "truncate",
                  isRail
                    ? "font-medium text-ds-slate-900"
                    : "font-semibold text-ds-mint-800"
                )}
              />
            }
          />
        </span>
      )}
      <span
        className={cn(
          "font-semibold text-ds-black",
          size === "row"
            ? "text-heading-xs"
            : isRail
              ? "line-clamp-2 text-footnote"
              : "text-ui-body"
        )}
      >
        {source.title}
      </span>
      {source.identity !== "" ? (
        <span
          className={cn(
            "font-normal",
            isRail
              ? "text-xs text-ds-slate-700"
              : "text-caption text-ds-slate-600"
          )}
        >
          {source.identity}
        </span>
      ) : source.resolution === "pending" ? (
        <span
          aria-hidden
          className={cn(SKELETON_PLACEHOLDER, "h-3 w-32 rounded-full")}
        />
      ) : null}
      {source.resolution === "pending" ? (
        <span
          aria-hidden
          className={cn(
            SKELETON_PLACEHOLDER,
            "h-5 w-20 self-start rounded-full"
          )}
        />
      ) : source.design === null ? null : (
        <Tag
          size={isRail ? "md" : "sm"}
          variant={source.design.variant}
          state="idle"
          icon={null}
          className="self-start"
        >
          {source.design.label}
        </Tag>
      )}
    </span>
  );
}

// The signals under a row; a placeholder holds their line while the api still resolves the source.
function ChatSourceSignals({
  source,
}: {
  source: ChatSource;
}): React.JSX.Element | null {
  const { quality, endorsement } = source.signals;
  if (quality !== null || endorsement !== null)
    return <SourceSignals quality={quality} endorsement={endorsement} />;
  return source.resolution === "pending" ? (
    <span
      aria-hidden
      data-testid="source-signals-placeholder"
      className={cn(SKELETON_PLACEHOLDER, "h-5 w-40 self-start rounded-full")}
    />
  ) : null;
}

function rowSpacing(index: number, size: "row" | "rail"): string {
  if (index === 0) return "";
  switch (size) {
    // The desktop rail spaces its rows, with no rule between.
    case "rail":
      return "mt-2";
    // A flat list, no card chrome, just a hairline between entries, matching
    // the divider above the list.
    case "row":
      return "mt-3 border-t border-ds-mint-800/15 pt-3";
    default:
      throw new Error(size satisfies never);
  }
}

// The skeleton's rows: where the journal line and the title's second line stop.
const SKELETON_ROWS = [
  { container: "w-1/3", titleEnd: "w-3/4" },
  { container: "w-1/4", titleEnd: "w-1/2" },
  { container: "w-2/5", titleEnd: "w-2/3" },
] as const;

/**
 * The list while its sources are on their way: numbered rows in the list's
 * layout, every slot held by the placeholder a row being resolved shows, so
 * the rows replace it without a jump.
 */
export function ChatSourcesSkeleton({
  size,
}: {
  size: "row" | "rail";
}): React.JSX.Element {
  const { t } = useTranslation();
  // The title's line box and glyph height: `text-heading-xs` (17/22) in a row,
  // `text-footnote` (13/20) in the rail.
  const titleLine = size === "row" ? "h-5.5" : "h-5";
  const titleBar = size === "row" ? "h-4" : "h-3";
  return (
    <div role="status" aria-label={t("Loading…")}>
      <ol aria-hidden className="m-0 flex list-none flex-col p-0">
        {SKELETON_ROWS.map((row, index) => (
          <li
            key={index}
            className={cn("flex items-start gap-2", rowSpacing(index, size))}
          >
            <span
              className={cn(SKELETON_PLACEHOLDER, "size-6 shrink-0 rounded")}
            />
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="flex h-5 items-center">
                <span
                  className={cn(
                    SKELETON_PLACEHOLDER,
                    "h-3 rounded-full",
                    row.container
                  )}
                />
              </span>
              <span className="flex flex-col">
                <span className={cn("flex items-center", titleLine)}>
                  <span
                    className={cn(
                      SKELETON_PLACEHOLDER,
                      "w-full rounded-full",
                      titleBar
                    )}
                  />
                </span>
                <span className={cn("flex items-center", titleLine)}>
                  <span
                    className={cn(
                      SKELETON_PLACEHOLDER,
                      "rounded-full",
                      titleBar,
                      row.titleEnd
                    )}
                  />
                </span>
              </span>
              <span
                className={cn(SKELETON_PLACEHOLDER, "h-3 w-32 rounded-full")}
              />
              <span
                className={cn(SKELETON_PLACEHOLDER, "h-5 w-20 rounded-full")}
              />
              <span
                className={cn(SKELETON_PLACEHOLDER, "h-5 w-40 rounded-full")}
              />
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** One row of the list: the source, and what the answer made of it. */
export type ChatSourceRow = {
  source: ChatSource;
  /** The number the answer's inline chips point at; null for a source the answer did not cite. */
  number: number | null;
};

/**
 * The list of everything one answer was grounded in, whatever the provenance
 * (Juisci library, PubMed, open web, an uploaded file). Rendered in the Sources
 * tab beside the answer.
 *
 * Each row carries the number the caller's numbering gave it, the same one the
 * answer's inline chips are resolved through.
 *
 * Presentational: hydrating an internal reference into a journal, an author
 * line and a study design belongs to the caller that owns the fetch, and so
 * does what a tapped row opens.
 */
export function ChatSources({
  rows,
  size,
  onSelect,
}: {
  rows: ChatSourceRow[];
  // `row` in the Sources tab, `rail` in the desktop context panel.
  size: "row" | "rail";
  // Opening the row's sheet. A row is not a link: the sheet is what carries the
  // full identity and the way out to the source, so tapping the row can only
  // ever mean "show me this one".
  onSelect: (index: number) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  if (rows.length === 0)
    return (
      <p className="m-0 py-8 text-center font-dm-sans text-footnote text-ds-slate-600">
        {t("No sources for this answer.")}
      </p>
    );

  return (
    <ol className="m-0 flex list-none flex-col p-0 font-dm-sans">
      {rows.map((row, index) => (
        <li key={row.source.key} className={rowSpacing(index, size)}>
          <button
            type="button"
            onClick={() => {
              onSelect(index);
            }}
            className="flex w-full cursor-pointer items-start gap-2 border-none bg-transparent p-0 text-left"
          >
            {row.number !== null && (
              <CitationChip number={row.number} size="md" />
            )}
            <span className="flex min-w-0 flex-1 flex-col gap-1">
              <ChatSourceIdentity source={row.source} size={size} />
              <ChatSourceSignals source={row.source} />
            </span>
          </button>
        </li>
      ))}
    </ol>
  );
}

/**
 * One source as a card (DS "SourceCard"): its number and where it was
 * published, its title, who wrote it and its design, then the way to it. A
 * citation's `HoverCard` shows it on desktop, previewing the row the Sources
 * list holds; clicking the citation still opens the sheet.
 */
export function SourceCard({
  source,
  number,
}: {
  source: ChatSource;
  number: number;
}): React.JSX.Element {
  const { t } = useTranslation();
  const meta = [
    ...(source.identity === "" ? [] : [source.identity]),
    ...(source.design === null ? [] : [source.design.label]),
  ];
  const linkClass =
    "font-dm-sans text-footnote font-medium text-ds-mint-800 no-underline hover:underline";
  return (
    <div className="flex flex-col gap-1.5 p-4 font-dm-sans">
      <div className="flex items-start gap-1.5">
        <CitationChip number={number} size="md" />
        {source.container !== null && (
          <span className="text-footnote text-ds-slate-700">
            {source.container.name}
          </span>
        )}
      </div>
      <p className="m-0 text-ui-body font-semibold text-ds-slate-900">
        {source.title}
      </p>
      {meta.length > 0 && (
        <p className="m-0 text-footnote text-ds-slate-700">
          {meta.join(" · ")}
        </p>
      )}
      {source.target === null ? null : source.target.kind === "internal" ? (
        <Link to={source.target.path} className={linkClass}>
          {t("Open article")}
        </Link>
      ) : (
        <a
          href={source.target.url}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          {source.container !== null && source.container.kind === "website"
            ? t("Open the source")
            : t("Open article")}
        </a>
      )}
    </div>
  );
}
